import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Building2, EyeOff, FlaskConical } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { AdminActionsMenu } from "@/components/admin/AdminActionsMenu";
import { EditRecordDrawer } from "@/components/admin/EditRecordDrawer";
import { FilterPanel } from "@/components/admin/FilterPanel";
import { BulkActionsBar } from "@/components/admin/BulkActionsBar";
import { useFilters } from "@/lib/useFilters";
import { useSelection } from "@/lib/useSelection";

const SCHOOL_FILTERS = [
  { key: "search", type: "search", label: "Search name or accreditation number",
    paths: ["name", "accreditation_number", "city", "state", "country"] },
  { key: "status", type: "select", label: "Accreditation status", path: "accreditation_status",
    options: [
      { value: "accredited", label: "Accredited" }, { value: "probation", label: "Probation" },
      { value: "suspended", label: "Suspended" }, { value: "revoked", label: "Revoked" },
    ] },
  { key: "accreditation_type", type: "select", label: "Scope", path: "accreditation_type",
    options: [{ value: "institutional", label: "Institutional" }, { value: "programmatic", label: "Programmatic" }] },
  { key: "city", type: "text", label: "City", path: "city" },
  { key: "state", type: "text", label: "State", path: "state" },
  { key: "country", type: "text", label: "Country", path: "country" },
  { key: "program", type: "search", label: "Program", paths: ["program_offerings"] },
  { key: "date_from", type: "dateFrom", label: "Issued from", path: "accredited_at" },
  { key: "date_to", type: "dateTo", label: "Expires by", path: "expires_at" },
  { key: "visible_in_registry", type: "boolean", label: "Visible in registry", path: "visible_in_registry" },
  { key: "is_archived", type: "boolean", label: "Archived", path: "is_archived" },
  { key: "is_sample", type: "boolean", label: "Sample", path: "is_sample" },
  { key: "is_deleted", type: "boolean", label: "Deleted", path: "is_deleted" },
];

export default function SchoolsAdmin() {
  const { user } = useAuth();
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const reload = () => {
    setLoading(true);
    api.get("/schools").then((r) => setSchools(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { reload(); }, []);

  const filters = useFilters(schools, SCHOOL_FILTERS, {
    defaultSort: { field: "accredited_at", dir: "desc" },
  });
  const selection = useSelection(filters.filtered);
  const isAdmin = user.role === "admin";

  return (
    <div className="animate-fade-up" data-testid="schools-admin-page">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 mb-2">ACIS · Approved schools</div>
        <h1 className="font-heading text-3xl font-bold text-primary flex items-center gap-3"><Building2 className="h-7 w-7" />Accredited institutions</h1>
        <p className="text-sm text-slate-500 mt-1">Active accreditations across institutional and program scopes.</p>
      </div>

      <FilterPanel
        schemas={SCHOOL_FILTERS}
        values={filters.values}
        set={filters.set}
        clear={filters.clear}
        count={filters.count}
        total={schools.length}
        activeCount={filters.activeCount}
        sortFields={[
          { key: "accredited_at", label: "Issue date" },
          { key: "expires_at", label: "Expiration date" },
          { key: "name", label: "Institution name" },
          { key: "accreditation_number", label: "Accreditation number" },
          { key: "accreditation_status", label: "Status" },
        ]}
        sort={filters.sort}
        setSort={filters.setSort}
        testid="schools-filters"
      />

      {isAdmin && <BulkActionsBar collection="schools" selection={selection} onDone={reload} />}

      <Card className="border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500">
              <tr>
                {isAdmin && (
                  <th className="p-4 w-10">
                    <Checkbox
                      checked={selection.allSelected}
                      onCheckedChange={selection.toggleAll}
                      data-testid="schools-select-all"
                    />
                  </th>
                )}
                <th className="p-4">Institution</th>
                <th className="p-4">Accreditation #</th>
                <th className="p-4">Status</th>
                <th className="p-4">Issued</th>
                <th className="p-4">Expires</th>
                <th className="p-4">Programs</th>
                {isAdmin && <th className="p-4 w-12"></th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && <tr><td colSpan={isAdmin ? 8 : 6} className="p-8 text-center text-slate-500">Loading…</td></tr>}
              {!loading && filters.filtered.length === 0 && (
                <tr><td colSpan={isAdmin ? 8 : 6} className="p-10 text-center">
                  <Building2 className="h-9 w-9 text-slate-300 mx-auto mb-2" />
                  <div className="text-sm text-slate-500">No accredited institutions match the current filters</div>
                </td></tr>
              )}
              {filters.filtered.map((s) => (
                <tr key={s.id} className="hover:bg-slate-50" data-testid={`school-row-${s.id}`}>
                  {isAdmin && (
                    <td className="p-4">
                      <Checkbox checked={selection.isSelected(s.id)} onCheckedChange={() => selection.toggle(s.id)} data-testid={`school-select-${s.id}`} />
                    </td>
                  )}
                  <td className="p-4">
                    <div className="font-medium text-primary flex items-center gap-2 flex-wrap">
                      {s.name}
                      {s.is_deleted && (
                        <span className="text-[9px] uppercase tracking-wider text-red-700 bg-red-50 border border-red-200 px-1.5 py-0.5 rounded">Deleted</span>
                      )}
                      {s.is_sample && (
                        <span className="text-[9px] uppercase tracking-wider text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded inline-flex items-center gap-1">
                          <FlaskConical className="h-2.5 w-2.5" /> Sample
                        </span>
                      )}
                      {s.visible_in_registry === false && (
                        <span className="text-[9px] uppercase tracking-wider text-slate-700 bg-slate-100 border border-slate-300 px-1.5 py-0.5 rounded inline-flex items-center gap-1">
                          <EyeOff className="h-2.5 w-2.5" /> Hidden
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500">{[s.city, s.state, s.country].filter(Boolean).join(", ")}</div>
                  </td>
                  <td className="p-4 font-mono-data text-xs text-primary">{s.accreditation_number}</td>
                  <td className="p-4"><StatusBadge status={s.accreditation_status} /></td>
                  <td className="p-4 text-xs text-slate-700">{s.accredited_at ? new Date(s.accredited_at).toLocaleDateString() : "—"}</td>
                  <td className="p-4 text-xs text-slate-700">{s.expires_at ? new Date(s.expires_at).toLocaleDateString() : "—"}</td>
                  <td className="p-4 text-xs">
                    {s.program_offerings?.slice(0, 2).join(", ")}
                    {s.program_offerings?.length > 2 && <span className="text-slate-400"> +{s.program_offerings.length - 2}</span>}
                  </td>
                  {isAdmin && (
                    <td className="p-2">
                      <AdminActionsMenu collection="schools" record={s} onEdit={setEditing} onChange={reload} />
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
      <EditRecordDrawer collection="schools" record={editing} open={!!editing} onClose={() => setEditing(null)} onSaved={reload} />
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    accredited: "bg-emerald-50 text-emerald-800 border-emerald-200",
    probation: "bg-amber-50 text-amber-800 border-amber-200",
    suspended: "bg-orange-50 text-orange-800 border-orange-200",
    withdrawn: "bg-slate-100 text-slate-700 border-slate-200",
    expired: "bg-slate-100 text-slate-600 border-slate-200",
    denied: "bg-red-50 text-red-800 border-red-200",
    pending: "bg-blue-50 text-blue-800 border-blue-200",
  };
  return <Badge variant="outline" className={`capitalize ${map[status] || map.pending}`}>{status}</Badge>;
}
